import type {
  ColumnFiltersState,
  ColumnOrderState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TABLE_STATE_KEYS } from '@/components/admin/shared/hooks/table-session-store'
import { ColumnContextMenu, ResizableHeader, TableControls } from './components'
import {
  CLIENT_COLUMN_ORDER,
  COLUMN_LABELS,
  DEFAULT_COLUMN_ORDER,
  DEFAULT_HIDDEN_COLUMNS,
  QUESTIONS_PER_PAGE,
} from './constants'
import { useColumnDefinitions } from './hooks/use-column-definitions'
import { problemsTableStore } from './hooks/use-session-storage'
import { useTableFilters } from './hooks/use-table-filters'
import type {
  CoachingQuestionHistory,
  ColumnWrappingState,
  ContextMenuState,
  MasterQuestionsTableProps,
} from './types'

const DEFAULT_SOURCES: string[] = []

export function MasterQuestionsTable({
  questions,
  sources = DEFAULT_SOURCES,
  onDeleteQuestion,
  onSelectQuestion,
  onUpdateQuestion,
  questionHistory,
  assignedHomework,
  onAssignHomework,
  persistState = false,
}: MasterQuestionsTableProps) {
  // Determine if we're in client context (client-specific columns available)
  const isClientContext = !!onAssignHomework

  // Build lookup maps for client-specific data
  const questionHistoryMap = useMemo(() => {
    if (!questionHistory) return new Map<string, CoachingQuestionHistory>()
    const map = new Map<string, CoachingQuestionHistory>()
    for (const h of questionHistory) {
      const id = h.problemId || h.questionId
      map.set(id, h)
    }
    return map
  }, [questionHistory])

  const assignedHomeworkSet = useMemo(() => {
    if (!assignedHomework) return new Set<string>()
    return new Set(
      assignedHomework
        .filter((a) => a.status === 'active' && a.type === 'PROBLEM')
        .map((a) => a.problemId)
        .filter((id): id is string => id != null),
    )
  }, [assignedHomework])

  // Use custom hooks for filters and column definitions
  const {
    globalFilter,
    setGlobalFilter,
    difficultyFilter,
    setDifficultyFilter,
    sourceFilter,
    setSourceFilter,
    studyPlanFilter,
    setStudyPlanFilter,
    tagsFilter,
    setTagsFilter,
    filteredQuestions,
    allStudyPlanNames,
    allTags,
    visibleCount,
    loadMore,
    globalFilterFn,
  } = useTableFilters({ questions, persist: persistState })

  const columns = useColumnDefinitions({
    questionHistoryMap,
    assignedHomeworkSet,
    onDeleteQuestion,
    onAssignHomework,
    isClientContext,
    tagsFilter,
  })

  // Default column visibility based on context
  // In client context, show externalId (override the default hidden state)
  const defaultColumnVisibility: VisibilityState = useMemo(
    () => ({
      ...DEFAULT_HIDDEN_COLUMNS,
      ...(isClientContext
        ? { externalId: true } // Show externalId in client context
        : { doneBefore: false, addHomework: false }),
    }),
    [isClientContext],
  )

  // Default column order based on context
  const defaultColumnOrder: ColumnOrderState = useMemo(
    () =>
      isClientContext ? [...CLIENT_COLUMN_ORDER] : [...DEFAULT_COLUMN_ORDER],
    [isClientContext],
  )

  // Default sorting based on context
  // In client context, sort by externalId ascending by default
  const defaultSorting: SortingState = useMemo(
    () => (isClientContext ? [{ id: 'externalId', desc: false }] : []),
    [isClientContext],
  )

  // Table state - initialize from session storage when persistState is true
  const [sorting, setSorting] = useState<SortingState>(() =>
    persistState
      ? problemsTableStore.getValue<SortingState>(
          TABLE_STATE_KEYS.SORTING,
          defaultSorting,
        )
      : defaultSorting,
  )
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  // In TanStack Table, visibility: false = hidden, true/undefined = visible
  // In client context: show addHomework and doneBefore (don't set to false)
  // In non-client context: hide addHomework and doneBefore (set to false)
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () =>
      persistState
        ? problemsTableStore.getValue<VisibilityState>(
            TABLE_STATE_KEYS.COLUMN_VISIBILITY,
            defaultColumnVisibility,
          )
        : defaultColumnVisibility,
  )
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() =>
    persistState
      ? problemsTableStore.getValue<ColumnOrderState>(
          TABLE_STATE_KEYS.COLUMN_ORDER,
          defaultColumnOrder,
        )
      : defaultColumnOrder,
  )

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)

  // Column text wrapping state
  const [columnWrapping, setColumnWrapping] = useState<ColumnWrappingState>(
    () =>
      persistState
        ? problemsTableStore.getValue<ColumnWrappingState>(
            TABLE_STATE_KEYS.COLUMN_WRAPPING,
            {},
          )
        : {},
  )

  // Persist table state to session storage when it changes
  useEffect(() => {
    if (!persistState) return
    problemsTableStore.setValue(TABLE_STATE_KEYS.SORTING, sorting)
  }, [persistState, sorting])

  useEffect(() => {
    if (!persistState) return
    problemsTableStore.setValue(
      TABLE_STATE_KEYS.COLUMN_VISIBILITY,
      columnVisibility,
    )
  }, [persistState, columnVisibility])

  useEffect(() => {
    if (!persistState) return
    problemsTableStore.setValue(TABLE_STATE_KEYS.COLUMN_ORDER, columnOrder)
  }, [persistState, columnOrder])

  useEffect(() => {
    if (!persistState) return
    problemsTableStore.setValue(
      TABLE_STATE_KEYS.COLUMN_WRAPPING,
      columnWrapping,
    )
  }, [persistState, columnWrapping])

  // Create table instance
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table API limitation
  const table = useReactTable({
    data: filteredQuestions,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnOrder,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onEnd',
  })

  // Get sizing info for smooth resize feedback
  const { columnSizingInfo } = table.getState()
  const isResizing = !!columnSizingInfo.isResizingColumn
  const deltaOffset = columnSizingInfo.deltaOffset ?? 0
  const tableWidth = isResizing
    ? table.getTotalSize() + deltaOffset
    : table.getTotalSize()

  // Context menu handlers
  const handleContextMenu = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      if (
        columnId === 'assign' ||
        columnId === 'delete' ||
        columnId === 'addHomework'
      )
        return

      e.preventDefault()
      setContextMenu({ x: e.clientX, y: e.clientY, columnId })
    },
    [],
  )

  const handleHideColumn = useCallback(() => {
    if (contextMenu) {
      setColumnVisibility((prev) => ({
        ...prev,
        [contextMenu.columnId]: false,
      }))
    }
  }, [contextMenu])

  const handleToggleWrap = useCallback(() => {
    if (contextMenu) {
      setColumnWrapping((prev) => ({
        ...prev,
        [contextMenu.columnId]: !prev[contextMenu.columnId],
      }))
    }
  }, [contextMenu])

  const handleToggleVisibility = (columnId: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: prev[columnId] === false,
    }))
  }

  const handleShowAll = () => {
    setColumnVisibility({})
  }

  return (
    <div>
      {/* Controls: Search, Filters, Column Visibility */}
      <TableControls
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        difficultyFilter={difficultyFilter}
        onDifficultyFilterChange={setDifficultyFilter}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        sources={sources}
        studyPlanFilter={studyPlanFilter}
        onStudyPlanFilterChange={setStudyPlanFilter}
        studyPlanOptions={allStudyPlanNames}
        tagsFilter={tagsFilter}
        onTagsFilterChange={setTagsFilter}
        tagsOptions={allTags}
        columnVisibility={columnVisibility}
        columnOrder={columnOrder}
        onToggleVisibility={handleToggleVisibility}
        onShowAllColumns={handleShowAll}
        onReorderColumns={setColumnOrder}
        isClientContext={isClientContext}
      />

      {/* Context Menu */}
      {contextMenu && (
        <ColumnContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          columnId={contextMenu.columnId}
          columnLabel={
            COLUMN_LABELS[contextMenu.columnId] || contextMenu.columnId
          }
          isWrapped={columnWrapping[contextMenu.columnId] ?? false}
          onHide={handleHideColumn}
          onToggleWrap={handleToggleWrap}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table
          style={{
            width: tableWidth,
            minWidth: '100%',
            tableLayout: 'fixed',
          }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-border border-b">
                {headerGroup.headers.map((header) => (
                  <ResizableHeader
                    key={header.id}
                    header={header}
                    columnSizingInfo={columnSizingInfo}
                    onContextMenu={handleContextMenu}
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-muted-foreground py-8 text-center"
                >
                  No questions found.
                </td>
              </tr>
            ) : (
              table
                .getRowModel()
                .rows.slice(0, visibleCount)
                .map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => onSelectQuestion(row.original)}
                    className="border-border hover:bg-muted/50 cursor-pointer border-b transition-colors"
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isCellResizing =
                        columnSizingInfo.isResizingColumn === cell.column.id
                      const cellWidth = isCellResizing
                        ? cell.column.getSize() + deltaOffset
                        : cell.column.getSize()
                      const isWrapped = columnWrapping[cell.column.id] ?? false
                      return (
                        <td
                          key={cell.id}
                          style={{ width: cellWidth }}
                          className={
                            isWrapped
                              ? 'overflow-hidden px-3 py-2 break-words whitespace-normal'
                              : 'overflow-hidden px-3 py-2 text-ellipsis whitespace-nowrap'
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      {/* Results count and Load More */}
      <div className="mt-3 flex flex-col items-center gap-3">
        <div className="text-muted-foreground text-xs">
          Showing {Math.min(visibleCount, table.getRowModel().rows.length)} of{' '}
          {table.getRowModel().rows.length} questions
          {table.getRowModel().rows.length !== questions.length &&
            ` (filtered from ${questions.length})`}
          {globalFilter && ` matching "${globalFilter}"`}
          {difficultyFilter && ` • ${difficultyFilter}`}
          {sourceFilter && ` • ${sourceFilter}`}
          {studyPlanFilter.length > 0 &&
            ` • Plans: ${studyPlanFilter.join(', ')}`}
          {tagsFilter.length > 0 && ` • Tags: ${tagsFilter.join(', ')}`}
        </div>

        {visibleCount < table.getRowModel().rows.length && (
          <button
            type="button"
            onClick={loadMore}
            className="border-border bg-muted hover:bg-muted/80 text-foreground flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
          >
            <ChevronDown size={16} />
            Load More (
            {Math.min(
              QUESTIONS_PER_PAGE,
              table.getRowModel().rows.length - visibleCount,
            )}{' '}
            more)
          </button>
        )}
      </div>
    </div>
  )
}
